import { CheckCircle, DownloadOutlined, Error as ErrorIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';

import CSVReader, { CSVReaderResult } from 'src/components/csv/csv-reader';
import { useTranslate } from 'src/locales';
import { useBulkCompanyImportMutation } from 'src/services/connections/connections.service';
import { TenantType } from 'src/types/enums';
import CSVTemplate from 'src/assets/templates/admin_import_companies.csv?url';

interface CompanyCSVRow {
  companyName: string;
  companyInformation?: string;
  companyType?: TenantType;
  taxId?: string;
  companyWebsite?: string;
  linkedinLink?: string;
  xLink?: string;
  facebookLink?: string;
  githubLink?: string;
  street?: string;
  city?: string;
  state?: string;
  countryCode?: string;
}

interface ImportResult {
  companyName: string;
  status: 'success' | 'failed';
  message?: string;
}

type Props = {
  organizationId?: string | undefined;
  open: boolean;
  onClose: VoidFunction;
};

enum ImportStep {
  UPLOAD = 'upload',
  PREVIEW = 'preview',
  RESULTS = 'results',
}

export default function BulkCompanyImportModal({ organizationId, open, onClose }: Props) {

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [bulkCompanyImport, { isLoading: isImporting }] = useBulkCompanyImportMutation();

  const [currentStep, setCurrentStep] = useState<ImportStep>(ImportStep.UPLOAD);
  const [parsedData, setParsedData] = useState<CompanyCSVRow[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

  const [resultsPage, setResultsPage] = useState(0);
  const [resultsRowsPerPage, setResultsRowsPerPage] = useState(10);

  const expectedHeaders = useMemo(() => [
    'Company Name',
    'Company Information',
    'Company Type',
    'Tax ID',
    'Company Website',
    'LinkedIn Link',
    'X Link',
    'Facebook Link',
    'Github Link',
    'Street',
    'City',
    'State',
    'Country Code',
  ], []);

  const handleCSVRead = useCallback((results: CSVReaderResult) => {
    setError(null);

    try {
      const { data } = results;

      if (!data || data.length === 0) {
        setError(t('companies.bulk_import_modal.errors.empty_file'));
        return;
      }

      const headers = data[0] as string[];

      if (!headers.includes('Company Name')) {
        setError(t('companies.bulk_import_modal.errors.invalid_headers'));
        return;
      }

      // Parse rows
      const rows: CompanyCSVRow[] = [];

      for (let i = 1; i < data.length; i++) {
        const row = data[i] as string[];

        if (!row || row.every((cell) => !cell || cell.trim() === '')) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const companyName = row[headers.indexOf('Company Name')]?.trim() || '';

        if (!companyName) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const companyTypeValue = row[headers.indexOf('Company Type')]?.trim() || '';
        let companyType: TenantType | undefined;
        if (companyTypeValue) {
          // map string to TenantType enum
          const typeMap: Record<string, TenantType> = {
            'recruiter': TenantType.Recruiter,
            'client': TenantType.Client,
            'candidate': TenantType.Candidate,
          };
          companyType = typeMap[companyTypeValue.toLowerCase()];
        }

        rows.push({
          companyName,
          companyInformation: row[headers.indexOf('Company Information')]?.trim() || '',
          companyType,
          taxId: row[headers.indexOf('Tax ID')]?.trim() || '',
          companyWebsite: row[headers.indexOf('Company Website')]?.trim() || '',
          linkedinLink: row[headers.indexOf('LinkedIn Link')]?.trim() || '',
          xLink: row[headers.indexOf('X Link')]?.trim() || '',
          facebookLink: row[headers.indexOf('Facebook Link')]?.trim() || '',
          githubLink: row[headers.indexOf('Github Link')]?.trim() || '',
          street: row[headers.indexOf('Street')]?.trim() || '',
          city: row[headers.indexOf('City')]?.trim() || '',
          state: row[headers.indexOf('State')]?.trim() || '',
          countryCode: row[headers.indexOf('Country Code')]?.trim() || '',
        });
      }

      if (rows.length === 0) {
        setError(t('companies.bulk_import_modal.errors.no_valid_rows'));
        return;
      }

      setParsedData(rows);
      setCurrentStep(ImportStep.PREVIEW);
    } catch (err) {
      console.error('CSV parsing error:', err);
      setError(t('companies.bulk_import_modal.errors.parse_failed'));
    }
  }, [t]);

  const handleImport = useCallback(async () => {
    setError(null);

    try {
      const importData = parsedData.map((company) => ({
        company_name: company.companyName,
        company_information: company.companyInformation || undefined,
        company_type: company.companyType || undefined,
        tax_id: company.taxId || undefined,
        company_website: company.companyWebsite || undefined,
        linkedin_link: company.linkedinLink || undefined,
        x_link: company.xLink || undefined,
        facebook_link: company.facebookLink || undefined,
        github_link: company.githubLink || undefined,
        street: company.street || undefined,
        city: company.city || undefined,
        state: company.state || undefined,
        country_code: company.countryCode || undefined,
      }));

      const response = await bulkCompanyImport({
        data: {
          organization_id: organizationId,
          companies: importData,
        }
      }).unwrap();

      setImportResults(response);
      setCurrentStep(ImportStep.RESULTS);
      enqueueSnackbar(t('companies.bulk_import_modal.import_success'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(t('companies.bulk_import_modal.errors.import_failed'), { variant: 'error' });
    }
  }, [parsedData, t, bulkCompanyImport, organizationId, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setImportResults([]);
    setError(null);
    setPreviewPage(0);
    setResultsPage(0);
    onClose();
  }, [onClose]);

  const handleBackToUpload = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setError(null);
    setPreviewPage(0);
  }, []);

  const resultStats = useMemo(() => {
    const successful = importResults.filter((r) => r.status === 'success').length;
    const failed = importResults.filter((r) => r.status === 'failed').length;
    return { successful, failed, total: importResults.length };
  }, [importResults]);

  const renderUploadStep = () => (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info">
        {t('companies.bulk_import_modal.upload_instructions')}
      </Alert>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('companies.bulk_import_modal.required_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={expectedHeaders[0]} size="small" color="primary" />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('companies.bulk_import_modal.optional_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {expectedHeaders.slice(1).map((header) => (
            <Chip key={header} label={header} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>

      <CSVReader onRead={handleCSVRead} />
    </Stack>
  );

  const renderPreviewStep = () => {
    const paginatedData = parsedData.slice(
      previewPage * previewRowsPerPage,
      previewPage * previewRowsPerPage + previewRowsPerPage
    );

    return (
      <Stack spacing={2}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="success">
          {t('companies.bulk_import_modal.preview_success', { count: parsedData.length })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('companies.bulk_import_modal.table.company_name')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.info')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.type')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.website')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.location')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.links')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{row.companyName}</TableCell>
                  <TableCell>
                    {row.companyInformation ? (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {row.companyInformation}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{row.companyType || '-'}</TableCell>
                  <TableCell>
                    {row.companyWebsite ? (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {row.companyWebsite}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {row.city || row.state || row.countryCode ? (
                      <Typography variant="body2" noWrap>
                        {[row.city, row.state, row.countryCode].filter(Boolean).join(', ')}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {row.linkedinLink && <Chip label="LinkedIn" size="small" />}
                      {row.xLink && <Chip label="X" size="small" />}
                      {row.facebookLink && <Chip label="Facebook" size="small" />}
                      {row.githubLink && <Chip label="Github" size="small" />}
                      {!row.linkedinLink && !row.xLink && !row.facebookLink && !row.githubLink && '-'}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={parsedData.length}
          page={previewPage}
          onPageChange={(_, newPage) => setPreviewPage(newPage)}
          rowsPerPage={previewRowsPerPage}
          onRowsPerPageChange={(e) => {
            setPreviewRowsPerPage(parseInt(e.target.value, 10));
            setPreviewPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  const renderResultsStep = () => {
    const paginatedResults = importResults.slice(
      resultsPage * resultsRowsPerPage,
      resultsPage * resultsRowsPerPage + resultsRowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Alert severity={resultStats.failed > 0 ? 'warning' : 'success'}>
          {t('companies.bulk_import_modal.results_summary', {
            successful: resultStats.successful,
            failed: resultStats.failed,
            total: resultStats.total,
          })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('companies.bulk_import_modal.table.status')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.company_name')}</TableCell>
                <TableCell>{t('companies.bulk_import_modal.table.result')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    {result.status === 'success' ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>{result.companyName}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={result.status === 'success' ? 'success.main' : 'error.main'}
                    >
                      {result.message}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={importResults.length}
          page={resultsPage}
          onPageChange={(_, newPage) => setResultsPage(newPage)}
          rowsPerPage={resultsRowsPerPage}
          onRowsPerPageChange={(e) => {
            setResultsRowsPerPage(parseInt(e.target.value, 10));
            setResultsPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {
          currentStep === ImportStep.UPLOAD && (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
              <Typography>
                {t('companies.bulk_import_modal.title')}
              </Typography>

              <Button
                startIcon={<DownloadOutlined />}
                href={CSVTemplate}
                download="Companies Import Template.csv"
              >
                {t('candidates.invites.candidate_invite_modal.download_template')}
              </Button>
            </Stack>
          )
        }
        {currentStep === ImportStep.PREVIEW && t('companies.bulk_import_modal.preview_title')}
        {currentStep === ImportStep.RESULTS && t('companies.bulk_import_modal.results_title')}
      </DialogTitle>

      <DialogContent>
        {currentStep === ImportStep.UPLOAD && renderUploadStep()}
        {currentStep === ImportStep.PREVIEW && renderPreviewStep()}
        {currentStep === ImportStep.RESULTS && renderResultsStep()}
      </DialogContent>

      <DialogActions>
        {currentStep === ImportStep.UPLOAD && (
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
        )}

        {currentStep === ImportStep.PREVIEW && (
          <>
            <Button onClick={handleBackToUpload}>{t('common.back')}</Button>
            <LoadingButton
              variant="contained"
              onClick={handleImport}
              loading={isImporting}
            >
              {t('companies.bulk_import_modal.import_button')}
            </LoadingButton>
          </>
        )}

        {currentStep === ImportStep.RESULTS && (
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}